import BookingCalendar from '../../bookingCalendar/BookingCalendar';
import I18n from '../../../config/i18n';
import DropdownSelect from '../../../utils/dropdownSelect/DropdownSelect';
import SubmitButton from './SubmitButton';
import { getOptionIndex } from '../../../utils/helpers';
import type { Prices } from '../../../features/prices/types';
import { Accommodation, Property } from '../../../routes/reducers/types';
import { CalendarEntry, CalendarEntryWithDate } from '../api/bookingsApi';
import Price from '../../price/components/Price';

import './AvailabilityWidget.css';
import PriceDetails from '../../price/components/PriceDetails';

interface ValueLabel {
  label: string;
  value: string | number;
}

interface AvailabilityWidgetProps {
  accommodation: Pick<
    Accommodation,
    'id' | 'costs' | 'display_title' | 'extraCosts' | 'extraOptions' | 'kind'
  >;
  property: Property;
  bookings: CalendarEntryWithDate;
  durationOfStay?: number;
  occupancy: number;
  arrival: moment.Moment;
  options: {
    durations: ValueLabel[];
    occupancies: ValueLabel[];
  };
  onDurationChange: (option: { value: number }) => void;
  onOccupancyChange: (option: { value: number }) => void;
  onDateChange: (date: moment.Moment) => void;
  selectedPrice: Prices | undefined;
  isAbsolutePrice: boolean;
  isPriceLoading: boolean;
  isPriceError: boolean;
  booking?: CalendarEntry;
}

export default function AvailabilityWidget({
  options,
  durationOfStay,
  onDurationChange,
  occupancy,
  onOccupancyChange,
  bookings,
  onDateChange,
  arrival,
  accommodation,
  property,
  selectedPrice,
  isAbsolutePrice,
  isPriceLoading,
  isPriceError,
  booking,
}: AvailabilityWidgetProps): JSX.Element {
  return (
    <div className="availability-widget">
      <h2 className="availability-widget-headline">{I18n.t('host.availability')}</h2>
      <div className="availability-widget-filter-item">
        <DropdownSelect
          options={options.durations}
          selectedIndex={getOptionIndex(durationOfStay, options.durations)}
          onChange={onDurationChange}
        />
      </div>
      <div className="availability-widget-filter-item">
        <DropdownSelect
          options={options.occupancies}
          selectedIndex={getOptionIndex(occupancy, options.occupancies)}
          onChange={onOccupancyChange}
        />
      </div>
      <BookingCalendar bookings={bookings} onDateChange={onDateChange} selected={arrival} />
      <div className="availability-widget-price">
        {isPriceError ? (
          <div className="availability-widget__error">
            {I18n.t('travel_booking_price.price_error')}
          </div>
        ) : (
          <>
            <Price
              hasMatchingPrice={!!selectedPrice?.matchingSuggestion}
              loading={isPriceLoading}
              alternativePrices={selectedPrice?.alternativeSuggestions || []}
              matchingPrice={selectedPrice?.matchingSuggestion || null}
              renderMatchingAndAlternatives
            />
            <div className="availability-widget-price-additionals">
              {selectedPrice?.matchingPrice && (
                <PriceDetails priceInfo={selectedPrice.matchingPrice} />
              )}
            </div>
          </>
        )}
      </div>
      <SubmitButton
        accommodation={accommodation}
        property={property}
        durationOfStay={durationOfStay}
        occupancy={occupancy}
        arrival={arrival}
        isAbsolutePrice={isAbsolutePrice}
        isPriceLoading={isPriceLoading}
        booking={booking}
        matchingPrice={selectedPrice?.matchingSuggestion || null}
      />
    </div>
  );
}

const defaultProps: Pick<AvailabilityWidgetProps, 'booking' | 'durationOfStay'> = {
  booking: undefined,
  durationOfStay: undefined,
};

AvailabilityWidget.defaultProps = defaultProps;
